
// @material-ui/core components

import React, { useState } from 'react';
import Label from "../Label/Label";

import Select, { ActionMeta, Props, StylesConfig } from 'react-select';

import { useTheme } from "@material-ui/core";

export type SelectOption = Record<string, unknown>;

type SelectChangeHandler = {
  bivarianceHack(value: unknown, action: { name?: string; action: string }): void;
}['bivarianceHack'];

export interface CustomSelectProps extends Omit<Props<SelectOption, boolean>, 'onChange'> {
  labelText?: string;
  onChange?: Props<SelectOption, boolean>['onChange'];
  onValueChange?: SelectChangeHandler;
  IsRequired?: boolean;
}

function mergeSelectStyles(
  styles?: StylesConfig<SelectOption, boolean>,
  usePortal?: boolean,
): StylesConfig<SelectOption, boolean> {
  const merged: StylesConfig<SelectOption, boolean> = {
    ...styles,
    menu: (base, state) => ({
      ...base,
      zIndex: 9999,
      ...(styles?.menu ? styles.menu(base, state) : {}),
    }),
  };

  if (usePortal) {
    merged.menuPortal = (base, state) => ({
      ...base,
      zIndex: 9999,
      ...(styles?.menuPortal ? styles.menuPortal(base, state) : {}),
    });
  }

  return merged;
}

export default function CustomSelect(props: CustomSelectProps) {
  const {
    name,
    labelText,
    onValueChange,
    labelProps,
    IsRequired,
    onChange,
    maxMenuHeight,
    menuPortalTarget,
    menuPosition,
    onMenuOpen,
    onMenuClose,
    styles,
    ...rest
  } = props;

  const theme = useTheme();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const usePortal = menuPortalTarget != null;
  const handleChange: NonNullable<Props<SelectOption, boolean>['onChange']> = (selectedValue, action) => {
    if (onChange) {
      onChange(selectedValue, action);
      return;
    }
    if (onValueChange) {
      const changeAction = action as ActionMeta<SelectOption> & { name?: string };
      onValueChange(selectedValue, { name: changeAction.name, action: changeAction.action });
    }
  };

  return (
    <div style={{ position: 'relative', zIndex: menuIsOpen ? 9999 : 1 }}>
      <Label labelText={labelText || name} IsRequired={IsRequired} />

      <Select<SelectOption, boolean>
        isClearable
        maxMenuHeight={maxMenuHeight || 220}
        name={name}
        onChange={handleChange}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        menuShouldScrollIntoView
        onMenuOpen={() => {
          setMenuIsOpen(true);
          onMenuOpen?.();
        }}
        onMenuClose={() => {
          setMenuIsOpen(false);
          onMenuClose?.();
        }}
        styles={mergeSelectStyles(styles, usePortal)}
        theme={selectTheme => ({
          ...selectTheme,
          colors: {
            ...selectTheme.colors,
            neutral0: theme.palette.background.paper,
            neutral80: theme.palette.mode === 'dark' ? theme.palette.dark.light : selectTheme.colors.neutral80
          },
        })}
        {...rest}
      />
    </div>
  )
}
